import React, { useEffect, useMemo, useState } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import SplashScreen from './components/pwa/SplashScreen';
import NetworkStatusBar from './components/NetworkStatusBar';
import PwaInstallPrompts from './components/pwa/PwaInstallPrompts';
import PwaUpdateSnackbar from './components/pwa/PwaUpdateSnackbar';
import { getSchoolSubdomainFromHost } from './config/platformHost';
import SchoolSubdomainApp from './pages/SchoolSubdomainApp';
import Homepage from './pages/Homepage';
import AdminDashboard from './pages/admin/AdminDashboard';
import StudentDashboard from './pages/student/StudentDashboard';
import TeacherDashboard from './pages/teacher/TeacherDashboard';
import UnifiedLogin from './pages/UnifiedLogin';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AdminRegisterPage from './pages/admin/AdminRegisterPage';
import SchoolPublicPage from './pages/public/SchoolPublicPage';
import SchoolLoginPage from './pages/public/SchoolLoginPage';
import StudentApplicationPage from './pages/public/StudentApplicationPage';
import SchoolDirectory from './pages/public/SchoolDirectory';
import LandingPage from './pages/LandingPage';
import DocumentationPage from './pages/DocumentationPage';
import Logout from './pages/Logout';
import RequireRole from './components/RequireRole';
import DefaultAppRedirect from './components/DefaultAppRedirect';
import PwaRootLanding from './components/pwa/PwaRootLanding';
import SubscriptionPaymentReturnPage from './pages/SubscriptionPaymentReturnPage';
import SystemDashboard from './pages/system/SystemDashboard';

const App = () => {
  const [appReady, setAppReady] = useState(false);

  const schoolSubdomain = useMemo(() => {
    if (typeof window === "undefined") return null;
    return getSchoolSubdomainFromHost(window.location.hostname);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAppReady(true);
      if (typeof window.__removeStaticSplash === "function") {
        window.__removeStaticSplash();
      }
    }, 1200);
    return () => clearTimeout(timer);
  }, []);

  if (!appReady) {
    return <SplashScreen />;
  }

  if (schoolSubdomain) {
    return (
      <Router>
        <NetworkStatusBar />
        <PwaInstallPrompts />
        <PwaUpdateSnackbar />
        <SchoolSubdomainApp subdomain={schoolSubdomain} />
      </Router>
    );
  }

  return (
    <Router>
      <NetworkStatusBar />
      <PwaInstallPrompts />
      <PwaUpdateSnackbar />
      <Routes>
        {/* Public — always available, even when logged in (session in localStorage) */}
        <Route path="/school/:slug/login" element={<SchoolLoginPage />} />
        <Route path="/school/:slug/apply" element={<StudentApplicationPage />} />
        <Route path="/school/:slug" element={<SchoolPublicPage />} />
        <Route path="/schools" element={<SchoolDirectory />} />
        <Route path="/platform" element={<LandingPage />} />
        <Route path="/docs/*" element={<DocumentationPage />} />
        <Route path="/" element={<PwaRootLanding browserElement={<Homepage />} />} />
        <Route path="/login" element={<UnifiedLogin />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/choose" element={<Navigate to="/login" replace />} />
        <Route path="/chooseasguest" element={<Navigate to="/login" replace />} />
        <Route path="/Adminlogin" element={<Navigate to="/login" replace />} />
        <Route path="/Studentlogin" element={<Navigate to="/login" replace />} />
        <Route path="/Teacherlogin" element={<Navigate to="/login" replace />} />
        <Route path="/Adminregister" element={<AdminRegisterPage />} />
        <Route path="/subscription-payment-return" element={<SubscriptionPaymentReturnPage />} />

        {/* Dashboards — always registered so /Admin/* matches before the catch-all */}
        <Route
          path="/Admin/*"
          element={
            <RequireRole role="Admin">
              <AdminDashboard />
            </RequireRole>
          }
        />
        <Route
          path="/Student/*"
          element={
            <RequireRole role="Student">
              <StudentDashboard />
            </RequireRole>
          }
        />
        <Route
          path="/Teacher/*"
          element={
            <RequireRole role="Teacher">
              <TeacherDashboard />
            </RequireRole>
          }
        />
        <Route
          path="/System/*"
          element={
            <RequireRole role="System">
              <SystemDashboard />
            </RequireRole>
          }
        />
        <Route path="/logout" element={<Logout />} />

        <Route path="*" element={<DefaultAppRedirect />} />
      </Routes>
    </Router>
  )
}

export default App
